import React, { useState, useEffect } from 'react';
import { getAuth, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import './App.css';

const tiers = [
  { id: 'starter', tokens: 100, price: 1.00, perToken: '$0.010', label: 'Starter' },
  { id: 'standard', tokens: 1000, price: 9.50, perToken: '$0.0095', label: 'Standard', popular: true },
  // The id stays 'pro': it is the Stripe price key and the webhook's pack id.
  // Only the label changed, so the pack no longer shares a name with the Pro plan.
  { id: 'pro', tokens: 5000, price: 45.00, perToken: '$0.009', label: 'Power' },
  { id: 'enterprise', tokens: 25000, price: 200.00, perToken: '$0.008', label: 'Enterprise' },
];

const models = [
  { name: 'Claude Sonnet 4', provider: 'Anthropic', badge: 'Best for code' },
  { name: 'Claude Opus 4', provider: 'Anthropic', badge: 'Most capable' },
  { name: 'GPT-4o', provider: 'OpenAI', badge: 'General purpose' },
  { name: 'Gemini 2.5 Flash', provider: 'Google', badge: 'Fastest' },
  { name: 'Gemini 2.5 Pro', provider: 'Google', badge: '1M context' },
  { name: 'o4-mini', provider: 'OpenAI', badge: 'Reasoning' },
  { name: 'Sonar Pro', provider: 'Perplexity', badge: 'Web search' },
];

export default function CloudPage() {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(null);
  const [success, setSuccess] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setSuccess(true);
      window.history.replaceState({}, '', '/cloud');
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser && !firebaseUser.isAnonymous) {
        setUser(firebaseUser);
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          setBalance(userDoc.data().computeTokenBalance || 0);
        }
      } else {
        setUser(null);
        setBalance(null);
      }
    });
    return () => unsubscribe();
  }, [success]);

  const handlePurchase = async (tier) => {
    setCheckoutError(null);

    if (!user) {
      setSigningIn(true);
      try {
        const authInstance = getAuth();
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        const result = await signInWithPopup(authInstance, provider);
        setUser(result.user);
        setSigningIn(false);
        // Re-trigger purchase now that we're signed in
        await startCheckout(tier, result.user);
      } catch (err) {
        setSigningIn(false);
        if (err.code !== 'auth/popup-closed-by-user') {
          setCheckoutError('Sign-in failed. Please try again.');
        }
      }
      return;
    }

    await startCheckout(tier, user);
  };

  const startCheckout = async (tier, currentUser) => {
    setLoading(tier.id);
    setCheckoutError(null);
    try {
      const functions = getFunctions();
      const createCheckout = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckout({
        tierId: tier.id,
        tokens: tier.tokens,
        priceUsd: tier.price,
      });
      if (result.data.url) {
        window.location.href = result.data.url;
      } else {
        setCheckoutError('Could not create checkout session. Please try again.');
        setLoading(null);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutError('Something went wrong starting checkout. Please try again or contact support@mchatai.com.');
      setLoading(null);
    }
  };

  return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#111827' }}>
      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '80px 24px 40px', maxWidth: 700, margin: '0 auto' }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>☁️</div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#111827', margin: '0 0 16px', lineHeight: 1.1 }}>
          mChatAI Cloud
        </h1>
        <p style={{ fontSize: '1.15rem', color: '#4b5563', lineHeight: 1.6, margin: '0 0 24px' }}>
          Access every AI model with pay-per-use compute tokens.
          No subscription needed. No API keys to manage. Just buy tokens and go.
        </p>
        <p style={{ fontSize: '0.95rem', color: '#6b7280', margin: '0 0 24px' }}>
          Monthly plans include tokens too. <Link to="/pricing" style={{ color: '#2563eb' }}>See plans</Link>
        </p>

        {success && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12,
            padding: '16px 24px', marginBottom: 24, color: '#166534', fontWeight: 500,
          }}>
            ✓ Payment successful! Your tokens have been credited. Return to mChatAI to start using them.
          </div>
        )}

        {checkoutError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 12,
            padding: '16px 24px', marginBottom: 24, color: '#991b1b',
          }}>
            {checkoutError}
          </div>
        )}

        {signingIn && (
          <div style={{
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12,
            padding: '12px 24px', marginBottom: 16, color: '#1e40af', fontSize: '0.9rem',
          }}>
            Signing you in first — complete the sign-in popup, then your purchase will continue automatically.
          </div>
        )}

        {balance !== null && (
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: '14px 24px', display: 'inline-block', marginBottom: 8,
          }}>
            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>Your balance: </span>
            <span style={{ color: '#111827', fontSize: '1.2rem', fontWeight: 700 }}>
              {balance.toFixed(1)} tokens
            </span>
            <span style={{ color: '#9ca3af', fontSize: '0.85rem', marginLeft: 8 }}>
              (${(balance * 0.01).toFixed(2)})
            </span>
          </div>
        )}
      </section>

      {/* How it works */}
      <section style={{ maxWidth: 700, margin: '0 auto 48px', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { step: '1', title: 'Buy Tokens', desc: 'Choose a tier below and pay with Stripe' },
            { step: '2', title: 'Open mChatAI', desc: 'Select "mChatAI Cloud" as your provider' },
            { step: '3', title: 'Use Any Model', desc: 'Claude, GPT, Gemini, Sonar — all available' },
          ].map((s) => (
            <div key={s.step} style={{ textAlign: 'center', padding: 16 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', background: '#3b82f6',
                color: '#fff', fontWeight: 700, fontSize: '1rem',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10,
              }}>{s.step}</div>
              <div style={{ color: '#111827', fontWeight: 600, marginBottom: 4 }}>{s.title}</div>
              <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing tiers */}
      <section style={{ maxWidth: 900, margin: '0 auto 64px', padding: '0 24px' }}>
        <h2 style={{ color: '#111827', fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', marginBottom: 32 }}>
          Choose Your Tier
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {tiers.map((tier) => (
            <article
              key={tier.id}
              style={{
                background: '#fff', borderRadius: 16, padding: 28, textAlign: 'center',
                position: 'relative',
                border: tier.popular ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                boxShadow: tier.popular ? '0 4px 24px rgba(59,130,246,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              {tier.popular && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: '#3b82f6', color: '#fff', fontSize: '0.7rem', fontWeight: 700,
                  padding: '3px 12px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>Most Popular</div>
              )}
              <div style={{ color: '#6b7280', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                {tier.label}
              </div>
              <div style={{ color: '#111827', fontSize: '2rem', fontWeight: 800, marginBottom: 4 }}>
                ${tier.price.toFixed(2)}
              </div>
              <div style={{ color: '#374151', fontSize: '0.9rem', marginBottom: 4, fontWeight: 500 }}>
                {tier.tokens.toLocaleString()} tokens
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: 24 }}>
                {tier.perToken} per token
              </div>
              <button
                onClick={() => handlePurchase(tier)}
                disabled={loading !== null || signingIn}
                style={{
                  width: '100%', padding: '11px 0', borderRadius: 24,
                  border: 'none', fontWeight: 700, fontSize: '0.9rem',
                  cursor: (loading !== null || signingIn) ? 'not-allowed' : 'pointer',
                  background: tier.popular ? '#3b82f6' : '#111827',
                  color: '#fff',
                  opacity: loading === tier.id ? 0.6 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {loading === tier.id ? 'Redirecting…' : signingIn ? 'Signing in…' : 'Buy Now'}
              </button>
            </article>
          ))}
        </div>
        {!user && (
          <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.82rem', marginTop: 16 }}>
            You'll be asked to sign in before checkout — your purchase will continue automatically.
          </p>
        )}
      </section>

      {/* Available models */}
      <section style={{ maxWidth: 700, margin: '0 auto 64px', padding: '0 24px' }}>
        <h2 style={{ color: '#111827', fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', marginBottom: 24 }}>
          Available Models
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {models.map((m) => (
            <div key={m.name} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 20px',
            }}>
              <div>
                <span style={{ color: '#111827', fontWeight: 600 }}>{m.name}</span>
                <span style={{ color: '#6b7280', fontSize: '0.85rem', marginLeft: 8 }}>{m.provider}</span>
              </div>
              <span style={{
                background: '#eff6ff', color: '#2563eb', fontSize: '0.7rem', fontWeight: 600,
                padding: '3px 10px', borderRadius: 8, border: '1px solid #bfdbfe',
              }}>{m.badge}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ maxWidth: 600, margin: '0 auto 80px', padding: '0 24px' }}>
        <h2 style={{ color: '#111827', fontSize: '1.3rem', fontWeight: 700, marginBottom: 20 }}>FAQ</h2>
        {[
          { q: 'What is a compute token?', a: '1 token = $0.01 in AI compute cost. A typical chat message costs 0.5–2 tokens depending on the model.' },
          { q: 'Do tokens expire?', a: 'No. Tokens never expire and carry forward indefinitely.' },
          { q: 'Can I use tokens on both iOS and macOS?', a: 'Yes. Your token balance syncs across all devices via your mChatAI account.' },
          { q: 'What happens when I run out?', a: 'Your existing API keys still work. mChatAI Cloud just becomes unavailable until you add more tokens.' },
        ].map((faq) => (
          <details key={faq.q} style={{ marginBottom: 4, borderBottom: '1px solid #f3f4f6', paddingBottom: 4 }}>
            <summary style={{ color: '#111827', cursor: 'pointer', padding: '10px 0', fontWeight: 500 }}>{faq.q}</summary>
            <p style={{ color: '#4b5563', margin: '4px 0 12px 16px', fontSize: '0.9rem', lineHeight: 1.6 }}>{faq.a}</p>
          </details>
        ))}
      </section>

      {/* Feedback Link */}
      <section style={{ maxWidth: 600, margin: '0 auto 80px', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <h2 style={{ color: '#111827', fontSize: '1.1rem', fontWeight: 700, marginBottom: 8 }}>Have an issue or idea?</h2>
          <p style={{ color: '#4b5563', fontSize: '0.95rem', marginBottom: 16 }}>Help us improve mChatAI by reporting bugs or requesting new features.</p>
          <Link to="/feedback" style={{
            display: 'inline-block', background: '#3b82f6', color: '#fff',
            padding: '10px 24px', borderRadius: '24px', textDecoration: 'none',
            fontWeight: 600, fontSize: '0.9rem',
          }}>
            Go to Feedback Board
          </Link>
        </div>
      </section>
    </div>
  );
}
